<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import type { LinkFragment } from '#graphql-operations'

const props = withDefaults(defineProps<{
  theme?: 'primary' | 'secondary' | 'negative' | 'scheme'
  link?: LinkFragment | undefined
  to?: RouteLocationRaw
  external?: boolean
  title?: string
  as?: 'button' | 'span'
}>(), {
  theme: 'primary',
  link: undefined,
  as: 'button',
})

const isHashLink = computed(() => typeof props.to === 'string' && props.to.startsWith('#'))

const buttonLabel = computed(() => props.title || props.link?.label || props.link?.elementTitle)

// useButtonHover('.text', 'y', 3)
</script>

<template>
  <Anchor
    v-if="to"
    :to
    :external
    :locale="!isHashLink"
    :as
    class="button relative"
    :aria-label="buttonLabel"
    :data-theme="theme"
  >
    <span class="px-7 motion-base rounded-2.5 inline-flex h-7.5 min-w-32 items-center justify-center">
      <slot>
        <Text
          v-if="buttonLabel"
          :text="buttonLabel"
          class="text typo-sans-1 inline-block uppercase capsize z-1"
        />
      </slot>
    </span>
  </Anchor>
  <CraftLink
    v-else-if="title || link"
    :link
    :as
    class="button relative"
    :aria-label="buttonLabel"
    :data-theme="theme"
  >
    <span class="px-7 motion-base rounded-2.5 inline-flex h-7.5 min-w-32 items-center justify-center">
      <slot>
        <Text
          v-if="buttonLabel"
          :text="buttonLabel"
          class="text typo-sans-1 inline-block uppercase capsize z-1"
        />
      </slot>
    </span>
  </CraftLink>
</template>

<style scoped>
.button {
  &:not([disabled]) {
    cursor: pointer;
  }
}

.button[data-theme='primary'] > span {
  color: var(--colors-white);
  background-color: var(--primary-color);
}

.button[data-theme='secondary'] > span {
  color: var(--colors-white);
  background-color: var(--accent-color);
}

.button[data-theme='negative'] > span {
  color: var(--primary-color);
  background-color: var(--negative-color);
}

/* Follows the surrounding color scheme, so it stays visible on any swatch (inverted included) */
.button[data-theme='scheme'] > span {
  color: var(--color-scheme-background);
  background-color: var(--color-scheme-foreground);
}

@media (hover: hover) and (pointer: fine) {
  .button[data-theme='primary']:hover > span,
  .button[data-theme='primary']:focus-visible > span {
    background-color: color-mix(in srgb, var(--primary-color) 80%, transparent);
  }

  .button[data-theme='secondary']:hover > span,
  .button[data-theme='secondary']:focus-visible > span {
    background-color: color-mix(in srgb, var(--accent-color) 80%, transparent);
  }

  .button[data-theme='negative']:hover > span,
  .button[data-theme='negative']:focus-visible > span {
    background-color: color-mix(in srgb, var(--negative-color) 80%, transparent);
  }

  .button[data-theme='scheme']:hover > span,
  .button[data-theme='scheme']:focus-visible > span {
    background-color: color-mix(in srgb, var(--color-scheme-foreground) 80%, transparent);
  }
}

.button.router-link-exact-active:not([href*='#']) {
  pointer-events: none;
}
</style>
